import React, { useEffect, useState } from "react";
import api from "../utils/axiosConfig";
import localforage from "localforage";
import ReactPaginate from "react-paginate";
import "../css/leaveRequestsAdmin.css";
import "../css/cabSummary.css";
import Dashboard from "./Dashboard";

const API_URL = process.env.REACT_APP_API_URL;

const CabRequestsRM = () => {
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [stateFilter, setStateFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;


  const fetchLeaveRequests = async (email) => {
    try {
      const token = await localforage.getItem("token");
      const response = await api.get(
        `${API_URL}/cab/cab-requests-reporting-manager`,
        {
          params: { receiverEmail: email },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const updatedRequests = response.data.cabRequests.map((request) => ({
        ...request,
        cabStatus: request.cabStatus || "pending",
      }));
      setLeaveRequests(updatedRequests);
      setFilteredRequests(updatedRequests);
    } catch (err) {
      setError("Error fetching leave requests");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getReceiverEmailFromLocalforage = async () => {
    try {
      const email = await localforage.getItem("email");
      if (email) {
        fetchLeaveRequests(email);
      } else {
        setError("Receiver email not found in localforage.");
      }
    } catch (err) {
      setError("Error retrieving email from localforage");
      console.error(err);
    }
  };

  const updateCabStatus = async (id, cabStatus) => {
    try {
      const token = await localforage.getItem("token");
      await api.put(
        `${API_URL}/cab/update-reporting-manager-cab-data/${id}`,
        { cabStatus }, // Removed unnecessary fields
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Update state to reflect changes immediately
      setLeaveRequests((prev) =>
        prev.map((request) =>
          request._id === id ? { ...request, cabStatus } : request
        )
      );
      setFilteredRequests((prev) =>
        prev.map((request) =>
          request._id === id ? { ...request, cabStatus } : request
        )
      );
    } catch (err) {
      setError(err.response?.data?.error || "Error updating cab status");
      console.error(err);
    }
  };

  // Search text and the State dropdown are applied together, so switching one
  // never wipes the other.
  const applyFilters = (query, stateValue, source) => {
    const list = source || leaveRequests;
    const q = (query || "").toLowerCase();
    const has = (v) => String(v || "").toLowerCase().includes(q);
    const next = list.filter((request) => {
      const matchesState =
        stateValue === "All" || (request.state || "") === stateValue;
      if (!matchesState) return false;
      if (!q) return true;
      return (
        has(request.state) ||
        has(request.name) ||
        has(request.leaveCode) ||
        has(request.zone) ||
        has(request.pcName)
      );
    });
    setFilteredRequests(next);
    setCurrentPage(0);
  };

  const handleStateFilter = (value) => {
    setStateFilter(value);
    applyFilters(searchQuery, value);
  };

  const handleSearch = (query) => {
    setSearchQuery(query);
    applyFilters(query, stateFilter);
  };

  useEffect(() => {
    getReceiverEmailFromLocalforage();
  }, []);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;

  const handlePageClick = (data) => {
    setCurrentPage(data.selected);
  };

  const pageCount = Math.ceil(filteredRequests.length / itemsPerPage);
  const displayedRequests = filteredRequests.slice(
    currentPage * itemsPerPage,
    (currentPage + 1) * itemsPerPage
  );

  // Punjab records store Region in `zone` and District in `pcName`, and have no
  // AC. Label the columns to match whichever state is being viewed.
  const isPunjabView = stateFilter === "Punjab";
  const isApView = stateFilter === "Andhra Pradesh";

  const formatTo12Hour = (time) => {
    const [hour, minute] = time.split(":");
    const h = parseInt(hour, 10);
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${minute} ${suffix}`;
  };

  return (
    <>
      <Dashboard />
      <div>
        <h2>Cab Requests</h2>
        <div
          className="search-bar"
          style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap" }}
        >
          <input
            type="text"
            placeholder="Search by name, state, zone or PC..."
            value={searchQuery}
            onChange={(e) => handleSearch(e.target.value)}
          />
          <select
            value={stateFilter}
            onChange={(e) => handleStateFilter(e.target.value)}
            style={{
              padding: "9px 12px",
              borderRadius: "8px",
              border: "1px solid #cfd8e3",
              background: "#fff",
              color: "#1b2b41",
              fontSize: "14px",
              minWidth: "180px",
            }}
            aria-label="Filter by state"
          >
            <option value="All">All States</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Punjab">Punjab</option>
          </select>
          <span style={{ fontSize: "13px", color: "#6a7c93" }}>
            {filteredRequests.length} request
            {filteredRequests.length === 1 ? "" : "s"}
          </span>
        </div>
        <div className="intervention-table">
          <table>
            <thead>
              <tr>
                <th>State</th>
                <th>Name</th>
                <th>Mobile (+91)</th>
                <th>{isPunjabView ? "Region" : isApView ? "Zone" : "Zone / Region"}</th>
                <th>
                  {isPunjabView ? "District" : isApView ? "PC Name" : "PC / District"}
                </th>
                {!isPunjabView && <th>AC Name</th>}
                <th>Team</th>
                <th>Designation</th>
                <th>Approx Kms</th>
                <th>Pick-up Location</th>
                <th>Date of Pick-up</th>
                <th>Pick-up Time</th>
                <th>Purpose</th>
                <th>Co-Passenger</th>
                <th>Action</th>
                <th>Summary</th>
              </tr>
            </thead>
            <tbody>
              {displayedRequests.map((request) => (
                <tr key={request._id}>
                  <td style={{ textAlign: "center" }}>{request.state || "-"}</td>
                  <td style={{ textAlign: "center" }}>{request.name}</td>
                  <td style={{ textAlign: "center" }}>{request.phoneNumber || "-"}</td>
                  <td style={{ textAlign: "center" }}>{request.zone || "-"}</td>
                  <td style={{ textAlign: "center" }}>{request.pcName || "-"}</td>
                  {!isPunjabView && (
                    <td style={{ textAlign: "center" }}>
                      {request.state === "Punjab" ? "—" : request.acName || "-"}
                    </td>
                  )}
                  <td style={{ textAlign: "center" }}>{request.team || "-"}</td>
                  <td style={{ textAlign: "center" }}>{request.designation || "-"}</td>
                  <td style={{ textAlign: "center" }}>{request.approxDriveKms || "-"}</td>
                  <td style={{ textAlign: "center", maxWidth: "200px" }}>
                    {request.pickupLocation}
                  </td>
                  <td style={{ textAlign: "center" }}>
                  {new Date(request.dateOfRequest).toLocaleDateString("en-GB")}
                </td>
                  <td style={{ textAlign: "center" }}>
                    {formatTo12Hour(request.pickupTime)}
                  </td>
                  <td>{request.purpose}</td>
                  <td style={{ textAlign: "center" }}>
                    {request.addOnPerson && request.addOnPerson.length > 0
                      ? request.addOnPerson
                          .map((person) => person.employeeName)
                          .join(", ")
                      : "N/A"}
                  </td>
                  <td
                    className={
                      request.cabStatus === "approved"
                        ? "status-approved"
                        : request.cabStatus === "not approved"
                        ? "status-not-approved"
                        : "status-pending"
                    }
                  >
                    <select
                      value={request.cabStatus}
                      onChange={(e) =>
                        updateCabStatus(request._id, e.target.value)
                      }
                      style={{ width: "100px" }}
                    >
                      <option value="approved">Approved</option>
                      <option value="not approved">Not Approved</option>
                      <option value="pending">Pending</option>
                    </select>
                  </td>
                  <td className="cab-summary">
                    <div className="cab-summary-card">
                      <div>
                        <span className="cab-summary-name">{request.name || "-"}</span>{" "}
                        <span className="cab-summary-role">
                          {request.designation || "-"}{request.team ? ` · ${request.team}` : ""}
                        </span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Mobile</span>
                        <span className="cab-summary-val">{request.phoneNumber || "-"}</span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">
                          {request.state === "Punjab" ? "Region" : "Zone"}
                        </span>
                        <span className="cab-summary-val">{request.zone || "-"}</span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">
                          {request.state === "Punjab" ? "District" : "PC"}
                        </span>
                        <span className="cab-summary-val">{request.pcName || "-"}</span>
                      </div>
                      {request.state !== "Punjab" && (
                        <div className="cab-summary-row">
                          <span className="cab-summary-label">AC</span>
                          <span className="cab-summary-val">{request.acName || "-"}</span>
                        </div>
                      )}
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Approx Kms</span>
                        <span className="cab-summary-val">{request.approxDriveKms || "-"}</span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Pick-up Loc</span>
                        <span className="cab-summary-val">
                          {request.pickupLocation ? (
                            <a href={request.pickupLocation} target="_blank" rel="noreferrer">
                              {request.pickupLocation}
                            </a>
                          ) : (
                            "-"
                          )}
                        </span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Date</span>
                        <span className="cab-summary-val">
                          {new Date(request.dateOfRequest).toLocaleDateString("en-GB")}
                        </span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Time</span>
                        <span className="cab-summary-val">{formatTo12Hour(request.pickupTime)}</span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Purpose</span>
                        <span className="cab-summary-val">{request.purpose || "-"}</span>
                      </div>
                      <div className="cab-summary-row">
                        <span className="cab-summary-label">Co-pax</span>
                        <span className="cab-summary-val">
                          {request.addOnPerson && request.addOnPerson.length > 0
                            ? request.addOnPerson.map((p) => p.employeeName).join(", ")
                            : "N/A"}
                        </span>
                      </div>
                      <div className="cab-summary-divider" />
                      <div>
                        <span className={`cab-status ${(request.cabStatus || "pending").replace(/\s+/g, "-")}`}>
                          {request.cabStatus || "pending"}
                        </span>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <ReactPaginate
            pageCount={pageCount}
            pageRangeDisplayed={5}
            marginPagesDisplayed={2}
            previousLabel={"previous"}
            nextLabel={"next"}
            breakLabel={"..."}
            onPageChange={handlePageClick}
            containerClassName={"pagination"}
            subContainerClassName={"pages pagination"}
            activeClassName={"active"}
          />
        </div>
      </div>
    </>
  );
};

export default CabRequestsRM;
